/**
 * MultiImageCaptureField.tsx
 *
 * Multi-image capture field component supporting two modes:
 * 1. Legacy Mode: Simple multi-capture up to max_images (default 10)
 * 2. Prompts Mode: Sequential prompted capture with specific labels and silhouettes
 *
 * Supports three value formats:
 * - File[] - during capture (legacy mode)
 * - string[] - loaded from backend (file paths)
 * - CapturedImage[] - prompts mode with promptId mapping
 */

import React, { useState, useEffect } from 'react';
import { Camera } from 'lucide-react';
import { BaseFieldProps } from './shared/types';
import { FieldWrapper } from './shared';
import { Button } from '../ui/button';
import { API_BASE_URL } from '../../environments/environment';
import { CapturedImage, ImagePrompt } from '../../types/types';
import { getResolvedSilhouetteUrl } from '../utils/silhouetteUtils';

export const MultiImageCaptureField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
  openCameraModal,
}) => {
  // State for silhouette loading errors
  const [showSilhouetteMulti, setShowSilhouetteMulti] = useState(true);

  // Managed blob URLs for File items — keyed by index (legacy) or promptId (prompts mode).
  // Effect revokes all URLs it created when value changes or component unmounts.
  const [blobUrlMap, setBlobUrlMap] = useState<Map<string, string>>(() => new Map());
  useEffect(() => {
    const currentImages = Array.isArray(value) ? value : [];
    const newUrls = new Map<string, string>();

    currentImages.forEach((item: any, idx: number) => {
      if (item instanceof File) {
        // Legacy mode: item is a File directly
        newUrls.set(idx.toString(), URL.createObjectURL(item));
      } else if (typeof item === 'object' && item !== null && 'file' in item && item.file instanceof File) {
        // Prompts mode: item is a CapturedImage with a File
        newUrls.set(item.promptId || idx.toString(), URL.createObjectURL(item.file));
      }
    });

    setBlobUrlMap(newUrls);
    return () => {
      newUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [value]);

  // Determine if using prompts mode or legacy mode
  const hasPrompts = fieldConfig.prompts && fieldConfig.prompts.length > 0;
  const imageCount = hasPrompts
    ? (fieldConfig.prompts?.length || 0)
    : (fieldConfig.max_images || 10);

  // Check if value is new format (CapturedImage[]) or legacy format (File[] | string[])
  const isNewFormat = Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === "object" &&
    value[0] !== null &&
    "promptId" in value[0];

  const images = Array.isArray(value) ? value : [];
  const atLimit = images.length >= imageCount;

  // Helper function to get captured images organized by prompt
  const getCapturedImagesMap = (): Map<string, CapturedImage> | null => {
    if (!hasPrompts) return null;

    const map = new Map<string, CapturedImage>();

    if (isNewFormat) {
      // New format: CapturedImage[] with promptId
      (images as CapturedImage[]).forEach(img => {
        map.set(img.promptId, img);
      });
    } else if (Array.isArray(images) && images.length > 0 && typeof images[0] === 'string') {
      // Loaded format: string[] (file paths from backend)
      // Map by array index to corresponding prompt
      (images as string[]).forEach((filePath, index) => {
        if (fieldConfig.prompts && fieldConfig.prompts[index]) {
          const prompt = fieldConfig.prompts[index];
          map.set(prompt.id, {
            promptId: prompt.id,
            promptLabel: prompt.label,
            file: filePath // File path string
          });
        }
      });
    }
    return map;
  };

  const capturedMap = getCapturedImagesMap();

  // Helper to get silhouette URL (generic for legacy mode)
  const getGenericSilhouette = (label: string) => {
    const imageName = label.toLowerCase().replace(/\s/g, '-');
    return `/assets/silhouettes/${imageName}.png`;
  };

  // Handle delete for legacy mode
  const handleDeleteLegacy = (idx: number) => {
    const newImages = images.filter((_: File | string, i: number) => i !== idx);
    onChange(fieldConfig.id, newImages);
  };

  // Handle delete for prompts mode
  const handleDeletePrompt = (promptId: string) => {
    if (isNewFormat) {
      // New format: filter by promptId
      const newImages = (images as CapturedImage[]).filter(
        img => img.promptId !== promptId
      );
      onChange(fieldConfig.id, newImages);
    } else {
      // String[] format: filter by index
      const newImages = (images as string[]).filter(
        (_, i) => fieldConfig.prompts && fieldConfig.prompts[i]?.id !== promptId
      );
      onChange(fieldConfig.id, newImages);
    }
  };

  // Handle camera modal opening
  const handleOpenCamera = (promptId?: string) => {
    if (openCameraModal) {
      openCameraModal(fieldConfig, promptId);
    }
  };

  // Preview mode - show disabled placeholder
  if (mode === 'preview') {
    return (
      <FieldWrapper
        field={fieldConfig}
        errors={errors}
        hasError={hasError}
        isPreview={true}
      >
        <div className="border-2 border-dashed rounded-md p-6 text-center bg-muted">
          <Camera className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {hasPrompts
              ? `Multi-Image Capture: ${fieldConfig.prompts?.length || 0} prompts`
              : `Multi-Image Capture: Up to ${imageCount} images`
            }
          </p>
          {hasPrompts && fieldConfig.prompts && (
            <div className="mt-4 space-y-2">
              {fieldConfig.prompts.map((prompt: ImagePrompt, idx: number) => (
                <div key={prompt.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-muted-foreground/20 text-xs">
                    {idx + 1}
                  </div>
                  <span>{prompt.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // Interactive modes (dashboard and public)
  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={false}
    >
      <div className="flex flex-col gap-2">
        {/* LEGACY MODE - Simple multi-capture */}
        {!hasPrompts && (
          <>
            {/* Capture button with count display */}
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenCamera()}
                disabled={disabled || atLimit}
              >
                {images.length > 0 ? "Add More Photos" : "Capture Photos"}
              </Button>
              <span className="text-sm text-muted-foreground">
                {images.length} / {imageCount}
              </span>
            </div>

            {/* Silhouette when no images */}
            {images.length === 0 && (
              <div className="mt-2 relative min-h-[200px] border-2 border-dashed rounded-md">
                {showSilhouetteMulti && (
                  <img
                    src={getGenericSilhouette(fieldConfig.label)}
                    alt="Silhouette"
                    className="absolute inset-0 w-full h-full object-contain opacity-40 pointer-events-none p-4"
                    onError={() => setShowSilhouetteMulti(false)}
                  />
                )}
              </div>
            )}

            {/* Thumbnail grid */}
            {images.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium mb-2">Captured Images:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 p-2">
                  {images.map((img: File | string, idx: number) => {
                    const imgSrc = img instanceof File
                      ? blobUrlMap.get(idx.toString())
                      : `${API_BASE_URL}/media/get-form_entry-file/${img}`;

                    return (
                      <div key={idx} className="relative w-20 h-20">
                        {imgSrc && <img
                          src={imgSrc}
                          alt={`Capture ${idx + 1}`}
                          className="w-full h-full object-cover border border-gray-300 rounded cursor-pointer hover:opacity-80 active:scale-95 active:opacity-70 transition-all"
                        />}
                        {!disabled && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 h-9 w-9 min-w-9 p-0 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white rounded-full shadow-lg border-2 border-white flex items-center justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLegacy(idx);
                            }}
                          >
                            <span className="text-base font-bold leading-none">×</span>
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* PROMPTS MODE - Sequential prompted capture */}
        {hasPrompts && (
          <div className="mt-2 space-y-3">
            {/* Show Start Capture button if no images captured yet */}
            {images.length === 0 && (
              <Button
                type="button"
                variant="default"
                onClick={() => handleOpenCamera()}
                disabled={disabled}
                className="w-full"
              >
                Start Capture ({fieldConfig.prompts?.length || 0} images)
              </Button>
            )}

            {/* Show captured images with re-capture buttons */}
            {images.length > 0 && fieldConfig.prompts && fieldConfig.prompts.map((prompt: ImagePrompt, idx: number) => {
              const capturedImage = capturedMap?.get(prompt.id);
              const imgSrc = capturedImage
                ? (typeof capturedImage.file === "string"
                    ? `${API_BASE_URL}/media/get-form_entry-file/${capturedImage.file}`
                    : (blobUrlMap.get(prompt.id) || null))
                : null;

              // Get prompt-specific silhouette or fall back to generic
              const resolvedSilhouette = prompt.silhouetteUrl
                ? getResolvedSilhouetteUrl(prompt.silhouetteUrl)
                : getGenericSilhouette(prompt.label);

              return (
                <div key={prompt.id} className="border rounded-md p-3 bg-muted/30">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                      {idx + 1}
                    </div>
                    <span className="text-sm font-medium">{prompt.label}</span>
                    {fieldConfig.required && !capturedImage && (
                      <span className="text-red-500 text-xs ml-auto">Required</span>
                    )}
                  </div>

                  {capturedImage ? (
                    <div className="flex gap-2 items-center">
                      <div className="relative w-32 h-32">
                        {imgSrc && <img
                          src={imgSrc}
                          alt={prompt.label}
                          className="w-full h-full object-cover border border-gray-300 rounded cursor-pointer hover:opacity-80 transition-opacity"
                        />}
                        {!disabled && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 h-8 w-8 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg border-2 border-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePrompt(prompt.id);
                            }}
                          >
                            <span className="text-base font-bold">×</span>
                          </Button>
                        )}
                      </div>
                      {!disabled && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenCamera(prompt.id)}
                        >
                          Re-capture
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Show silhouette for uncaptured prompts */}
                      {resolvedSilhouette && (
                        <div className="relative w-full h-32 border-2 border-dashed rounded-md mb-2 overflow-hidden">
                          <img
                            src={resolvedSilhouette}
                            alt={`${prompt.label} silhouette`}
                            className="absolute inset-0 w-full h-full object-contain opacity-30 pointer-events-none p-2"
                            onError={(e) => {
                              // Hide silhouette if it fails to load
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenCamera(prompt.id)}
                        disabled={disabled}
                        className="w-full"
                      >
                        Capture {prompt.label}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}

            {/* Image count display */}
            <div className="text-xs text-muted-foreground mt-2">
              {capturedMap ? capturedMap.size : 0} / {fieldConfig.prompts?.length || 0} images captured
            </div>
          </div>
        )}
      </div>
    </FieldWrapper>
  );
});

MultiImageCaptureField.displayName = 'MultiImageCaptureField';
